import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import ChairRoundedIcon from '@mui/icons-material/ChairRounded'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded'

import { Network } from '@/core/models/network.model'
import { useGetPlanningOverviewQuery } from '@/core/services/planning'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'
import { isConsultationOrder, isExpertiseOrder } from '@/utils/type-guards'

type NetworkDailyOverviewProps = {
  isNetworkAdmin: boolean
  isNetworkMember: boolean
  network: Network
}

type NetworkOverviewTile = {
  count: number
  icon: JSX.Element
  label: string
  priority?: boolean
  to: string
}

export function NetworkDailyOverview({ isNetworkAdmin, isNetworkMember, network }: NetworkDailyOverviewProps) {
  const { t } = useTranslation()

  const { data: planningOverview = [], isLoading: isLoadingPlannings } = useGetPlanningOverviewQuery(
    { includeArchived: false, networkId: network._id },
    { skip: !isNetworkMember }
  )
  const { waitingRoomCount = 0, isLoadingWaitingRooms } = useListWaitingRoomsQuery(
    { query: { network: network._id }, options: { limit: 0 } },
    {
      skip: !isNetworkMember,
      selectFromResult: ({ data, isLoading }) => ({
        isLoadingWaitingRooms: isLoading,
        waitingRoomCount: data?.items?.length ?? 0,
      }),
    }
  )

  const consultationOrderCount = network.pendingOrders.filter(isConsultationOrder).length
  const expertiseOrderCount = network.pendingOrders.filter(isExpertiseOrder).length
  const attentionPlanningCount = planningOverview.filter((planning) => planning.attentionLevel !== 'normal').length
  const activePlanningCount = planningOverview.filter((planning) => planning.status === 'active').length
  const planningCount = attentionPlanningCount || activePlanningCount || planningOverview.length

  const tiles: NetworkOverviewTile[] = [
    ...(isNetworkMember
      ? [
          {
            count: planningCount,
            icon: <CalendarMonthRoundedIcon />,
            label:
              attentionPlanningCount > 0
                ? t('page.network.overview.planningsNeedAttention', { defaultValue: 'Plannings need attention' })
                : t('page.network.overview.activePlannings', { defaultValue: 'Active plannings' }),
            priority: attentionPlanningCount > 0,
            to: `/networks/${network._id}/plannings`,
          },
          {
            count: waitingRoomCount,
            icon: <ChairRoundedIcon />,
            label: t('page.network.overview.waitingRooms', { defaultValue: 'Waiting rooms' }),
            to: `/networks/${network._id}/waiting-rooms`,
          },
        ]
      : []),
    {
      count: consultationOrderCount,
      icon: <AssignmentTurnedInRoundedIcon />,
      label: t('page.network.overview.consultationRequests', { defaultValue: 'Consultation requests' }),
      priority: consultationOrderCount > 0,
      to: `/networks/${network._id}/consultation`,
    },
    {
      count: expertiseOrderCount,
      icon: <AssignmentTurnedInRoundedIcon />,
      label: t('page.network.overview.expertiseRequests', { defaultValue: 'Expertise requests' }),
      priority: expertiseOrderCount > 0,
      to: `/networks/${network._id}/expertise`,
    },
    ...(isNetworkAdmin
      ? [
          {
            count: network.pendingMembers.length,
            icon: <PersonAddAlt1RoundedIcon />,
            label: t('page.network.overview.memberRequests', { defaultValue: 'Member requests' }),
            priority: network.pendingMembers.length > 0,
            to: `/networks/${network._id}/member-requests`,
          },
        ]
      : [
          {
            count: network.members.length,
            icon: <GroupsRoundedIcon />,
            label: t('page.network.overview.members', { defaultValue: 'Members' }),
            to: `/networks/${network._id}/members`,
          },
        ]),
    ...(network.chatRoom
      ? [
          {
            count: 0,
            icon: <ForumRoundedIcon />,
            label: t('page.network.overview.chat', { defaultValue: 'Chat' }),
            to: `/networks/${network._id}/chat?id=${network.chatRoom}`,
          },
        ]
      : []),
  ]

  return (
    <Stack spacing={1.5} data-testid="network-daily-overview">
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {t('page.network.overview.title', { defaultValue: 'Today in this network' })}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}
          >
            {t('page.network.overview.subtitle', { defaultValue: 'Planning, requests, and rooms that drive daily work' })}
          </Typography>
        </Box>
        {(attentionPlanningCount > 0 || consultationOrderCount > 0 || expertiseOrderCount > 0 || (isNetworkAdmin && network.pendingMembers.length > 0)) && (
          <Chip color="warning" label={t('page.network.overview.needsAttention', { defaultValue: 'Attention' })} size="small" sx={{ flexShrink: 0 }} />
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(6, minmax(0, 1fr))',
          },
        }}
      >
        {tiles.map((tile) => (
          <Box
            key={tile.to + tile.label}
            component={Link}
            to={tile.to}
            sx={{
              alignItems: 'center',
              border: 1,
              borderColor: tile.priority ? 'warning.main' : 'divider',
              borderRadius: 1,
              color: 'inherit',
              display: 'flex',
              gap: 1,
              minHeight: 74,
              minWidth: 0,
              overflow: 'hidden',
              p: 1.25,
              textDecoration: 'none',
              transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
              '& .MuiSvgIcon-root': {
                fontSize: 20,
              },
              '&:hover': {
                borderColor: tile.priority ? 'warning.dark' : 'primary.main',
                boxShadow: 1,
              },
            }}
          >
            <Box sx={{ color: tile.priority ? 'warning.main' : 'primary.main', display: 'flex', flexShrink: 0 }}>{tile.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isLoadingPlannings && tile.to.endsWith('/plannings') ? (
                <Skeleton width={36} />
              ) : isLoadingWaitingRooms && tile.to.endsWith('/waiting-rooms') ? (
                <Skeleton width={36} />
              ) : (
                <Typography variant="h6" fontWeight={700} lineHeight={1}>
                  {tile.count}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}
              >
                {tile.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Stack>
  )
}
